import { Airfield } from './airfield';
import { INITIAL_RATING } from './airlines';
import { type Building, type BuildingTypeId, buildingDef, footprint, isRunway } from './buildings';
import { STARTING_MONEY } from './catalog';
import {
  ADMIN_MONEY,
  AdminMode,
  type Command,
  type CommandContext,
  type CommandType,
  executeCommand,
  type RejectReason,
} from './commands';
import { DEFAULT_MAP_HEIGHT, DEFAULT_MAP_WIDTH } from './constants';
import type { DailyGoal } from './daily';
import { FEE_DEFAULT, type Loan } from './finance';
import { Ground } from './ground';
import { FNV_OFFSET, fnvBytes, fnvSafeInt, fnvU32 } from './hash';
import {
  type Aircraft,
  type Contract,
  dayIndex,
  emptyLandside,
  emptyLedger,
  type Flight,
  type LandsideFlow,
  type Ledger,
  type Offer,
  type OpsEvent,
  type OpsWorld,
  REPORT_FIELDS,
  type RunwayClosure,
  type TransferGroup,
  updateOperations,
} from './operations';
import { INITIAL_CATEGORY, RATING_CATEGORIES } from './reputation';
import { Rng } from './rng';
import type { RunwaySnow, SnowJob } from './snow';
import { ALL_AUTO } from './staff';
import { MapTheme, mapTheme, typicalClimate } from './theme';
import { INITIAL_CLIMATE, INITIAL_SKY, INITIAL_VISIBILITY, type SkyId } from './weather';
import { INITIAL_WIND_DIR, INITIAL_WIND_SPEED } from './wind';

export interface WorldOptions {
  seed: number;
  width?: number;
  height?: number;
  startingMoney?: number;
  /** The map (MapTheme, theme.ts); the green countryside if left out. */
  theme?: number;
}

export interface IssuedCommand {
  readonly playerId: number;
  readonly command: Command;
}

/**
 * The unit of lockstep. The relay numbers bundles consecutively; every peer applies the same
 * bundles in the same order. `advance` is false while the game is paused: the commands are
 * applied (players may build while paused) but game time does not move.
 */
export interface TickBundle {
  readonly seq: number;
  readonly advance: boolean;
  readonly commands: readonly IssuedCommand[];
}

export type SimEvent =
  | {
      readonly type: 'commandApplied';
      readonly playerId: number;
      readonly commandType: CommandType;
      readonly cost: number;
    }
  | {
      readonly type: 'commandRejected';
      readonly playerId: number;
      readonly commandType: CommandType;
      readonly reason: RejectReason;
    }
  | {
      readonly type: 'constructionComplete';
      readonly buildingId: number;
      readonly buildingType: BuildingTypeId;
      readonly level: number;
    }
  | OpsEvent;

export class World implements CommandContext, OpsWorld {
  /** Number of bundles applied so far; the next bundle must carry this seq. */
  seq = 0;
  /** Game ticks elapsed. Only advances while unpaused. */
  time = 0;
  /** Integer cents. May go negative (upkeep, penalties). */
  money: number;
  /** Ordered by id (creation order), which keeps every iteration deterministic. */
  readonly buildings: Building[] = [];
  nextBuildingId = 1;
  readonly startingParcels: number;

  // Airline operations (all ordered by id).
  readonly offers: Offer[] = [];
  readonly contracts: Contract[] = [];
  readonly flights: Flight[] = [];
  readonly aircraft: Aircraft[] = [];
  /** Shared id counter for offers, contracts, flights and aircraft. */
  nextOpsId = 1;
  today: Ledger = emptyLedger(dayIndex(0));
  yesterday: Ledger = emptyLedger(dayIndex(0) - 1);
  /** Every finished day added up: the report book's all time (see operations.ts addLedger). */
  allTime: Ledger = emptyLedger(0);
  /** Overall rating 0–1000 and its categories (see reputation.ts), and the online buzz. */
  rating = INITIAL_RATING;
  ratings: number[] = new Array<number>(RATING_CATEGORIES).fill(INITIAL_CATEGORY);
  buzz = 0;
  /** Runways closed after an emergency, until a tick. */
  readonly runwayClosures: RunwayClosure[] = [];
  /** Milestones reached (bits) and departures served per aircraft class (index = class). */
  milestones = 0;
  readonly departuresByClass: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  /** Compass step (0 = N, 4 = E, …) the wind blows from, and its speed in knots. */
  windDir = INITIAL_WIND_DIR;
  windSpeed = INITIAL_WIND_SPEED;
  /** Sky condition, visibility (100 m units) and mean temperature (°C) of the current days. */
  oilIndex = 100;
  economy = 0;
  marketFlags = 0;
  /** Admin mode (see commands.ts setAdminMode): AdminMode bits, on now and ever used. */
  adminMode = 0;
  /** Steps of the sound insulation programme paid (see airlines.ts noiseQuota). */
  noiseInsulation = 0;
  /**
   * An airport from before widebodies needed a large stand (snapshot v50), with widebody
   * contracts and no large stand: they may still use a level 2 medium stand until the first
   * large stand is built (see airfield.ts standTakes).
   */
  legacyWideStands = false;
  /**
   * The map the game is played on (MapTheme, theme.ts): set when the world is made and never
   * changed. The weather follows it; the client draws the land to match.
   */
  theme = 0;
  sky: SkyId = INITIAL_SKY;
  readonly skyAhead: number[] = [];
  visibility = INITIAL_VISIBILITY;
  climate = INITIAL_CLIMATE;
  /** State incentive grant: state, amount and deadline (see grant.ts). */
  grantState = 0;
  grantAmount = 0;
  grantDeadline = 0;
  /** The airport's name, chosen by the players ('' until named). */
  airportName = '';
  /** People hired per staff role, and the roles on automatic staffing (see staff.ts). */
  staffHired: number[] = [0, 0, 0, 0];
  staffAuto = ALL_AUTO;
  /** Per role: percent on the night shift (see staff.ts). */
  staffNight: number[] = [100, 100, 100, 100];
  /** Pay policy and strike (see staff.ts). */
  wagePct = 100;
  strikeUntil = 0;
  /** Noise quota points used since the night began. */
  nightNoise = 0;
  /** Connecting passengers waiting airside (see operations.ts). */
  readonly transferPool: TransferGroup[] = [];
  /** Snow on each runway and plough teams out; how hard it snows; the winter's blizzard
   * (planned for, and lasting until; see snow.ts). */
  readonly runwaySnow: RunwaySnow[] = [];
  readonly snowJobs: SnowJob[] = [];
  /** Today's goals (see daily.ts). */
  readonly dailyGoals: DailyGoal[] = [];
  /** VIPs who left pleased, and air shows flown, ever. */
  vipsPleased = 0;
  airshows = 0;
  /** The airlines that have flown from here, by id, in order (the airline album). */
  readonly airlinesSeen: number[] = [];
  snowfall = 0;
  blizzardAt = 0;
  blizzardUntil = 0;
  /** Marketing (see marketing.ts): next social campaign, end of the roadshow (ticks). */
  socialReadyAt = 0;
  promoUntil = 0;
  /** Loans being repaid (ordered by id) and fee settings (see finance.ts). */
  readonly loans: Loan[] = [];
  landingFeePct = FEE_DEFAULT;
  paxFeePct = FEE_DEFAULT;

  /** Render bookkeeping: set whenever a building changes. Not simulation state. */
  buildingsDirty = true;
  /**
   * How the roads in coped last minute: worked out afresh every minute from the state, read
   * only by the display. Not simulation state (never saved or hashed).
   */
  landside: LandsideFlow = emptyLandside();

  /** Derived taxi network; rebuilt lazily after the map or buildings change. */
  private airfieldCache: Airfield | null = null;
  private events: SimEvent[] = [];

  constructor(
    readonly seed: number,
    readonly ground: Ground,
    readonly rng: Rng,
    money: number,
  ) {
    this.money = money;
    const probe = new Ground(ground.width, ground.height);
    probe.grantStartingParcels();
    this.startingParcels = probe.ownedCount();
  }

  static create(opts: WorldOptions): World {
    const ground = new Ground(opts.width ?? DEFAULT_MAP_WIDTH, opts.height ?? DEFAULT_MAP_HEIGHT);
    ground.grantStartingParcels();
    ground.layHighway();
    const world = new World(
      opts.seed >>> 0,
      ground,
      new Rng(opts.seed),
      opts.startingMoney ?? STARTING_MONEY,
    );
    // Another map starts in its own spring (the green one keeps its mild first day).
    const theme = mapTheme(opts.theme ?? MapTheme.Temperate);
    if (theme !== MapTheme.Temperate) {
      world.theme = theme;
      world.climate = typicalClimate(theme, 0);
    }
    return world;
  }

  step(bundle: TickBundle): void {
    if (bundle.seq !== this.seq) {
      throw new Error(`Bundle out of order: expected seq ${this.seq}, got ${bundle.seq}`);
    }
    for (const { playerId, command } of bundle.commands) {
      const plan = executeCommand(this, command);
      if (plan.ok) {
        this.money -= plan.cost;
        this.airfieldCache = null;
        if (command.type !== 'buildNetwork' && command.type !== 'removeNetwork') {
          this.buildingsDirty = true;
        }
        this.events.push({
          type: 'commandApplied',
          playerId,
          commandType: command.type,
          cost: plan.cost,
        });
      } else {
        this.events.push({
          type: 'commandRejected',
          playerId,
          commandType: command.type,
          reason: plan.reason ?? 'invalidCommand',
        });
      }
    }
    if (bundle.advance) {
      this.updateConstruction();
      updateOperations(this);
      this.time++;
    }
    // Admin mode: money never runs out.
    if (this.adminMode & AdminMode.On && this.money < ADMIN_MONEY) this.money = ADMIN_MONEY;
    this.seq++;
  }

  airfield(): Airfield {
    this.airfieldCache ??= new Airfield(this.ground, this.buildings);
    return this.airfieldCache;
  }

  emit(event: OpsEvent): void {
    this.events.push(event);
  }

  private updateConstruction(): void {
    for (const b of this.buildings) {
      if (b.buildRemaining <= 0) continue;
      b.buildRemaining--;
      this.buildingsDirty = true;
      if (b.buildRemaining === 0) {
        b.level++;
        // A runway upgrade lays new pavement.
        if (isRunway(b.type)) b.wear = 0;
        b.buildTotal = 0;
        this.airfieldCache = null;
        this.events.push({
          type: 'constructionComplete',
          buildingId: b.id,
          buildingType: b.type,
          level: b.level,
        });
      }
    }
  }

  /** Scenario set-up only (see scenario.ts): every building site is finished at once. */
  completeConstruction(): void {
    for (const b of this.buildings) {
      if (b.buildRemaining <= 0) continue;
      b.buildRemaining = 0;
      b.buildTotal = 0;
      b.level++;
    }
    this.buildingsDirty = true;
    this.airfieldCache = null;
  }

  /** Rebuilds the derived occupancy map from the building list (after loading). */
  rebuildOccupancy(): void {
    this.ground.occupancy.fill(0);
    for (const b of this.buildings) {
      const def = buildingDef(b.type);
      if (!def) continue;
      const { w, h } = footprint(def, b.rot);
      this.ground.fillOccupancy(b.x, b.y, w, h, b.id);
    }
  }

  drainEvents(): SimEvent[] {
    const out = this.events;
    this.events = [];
    return out;
  }
}

function hashLedger(h: number, l: Ledger): number {
  let x = fnvU32(h, l.day);
  for (const v of [
    l.landing,
    l.passenger,
    l.retail,
    l.fuel,
    l.parking,
    l.upkeep,
    l.penalties,
    l.handling,
  ]) {
    x = fnvSafeInt(x, v);
  }
  for (const v of [
    l.departures,
    l.passengers,
    l.missed,
    l.onTime,
    l.delayMinutes,
    l.cancelled,
    l.lostArrival,
    l.lostVehicles,
    l.lostPassengers,
    l.lostDeparture,
    l.lostTechnical,
    l.lostInbound,
    l.hotelGuests,
    l.transfers,
    l.missedConnections,
    l.lostWeather,
    l.bagsLost,
  ]) {
    x = fnvU32(x, v);
  }
  x = fnvSafeInt(x, l.maintenance);
  x = fnvSafeInt(x, l.wages);
  x = fnvSafeInt(x, l.loans);
  x = fnvSafeInt(x, l.transit);
  x = fnvSafeInt(x, l.rewards);
  x = fnvSafeInt(x, l.cargo);
  x = fnvSafeInt(x, l.noiseFines);
  x = fnvSafeInt(x, l.hotel);
  x = fnvSafeInt(x, l.overdraft);
  x = fnvSafeInt(x, l.catering);
  x = fnvSafeInt(x, l.marketing);
  x = fnvSafeInt(x, l.military);
  x = fnvSafeInt(x, l.aircraftParking);
  x = fnvSafeInt(x, l.tolls);
  x = fnvSafeInt(x, l.commerce);
  x = fnvU32(x, l.customers);
  x = fnvSafeInt(x, l.rent);
  x = fnvU32(x, l.tonnes);
  return x;
}

/**
 * Hash of the complete simulation state. Co-op peers compare it to detect desyncs, so it must
 * cover everything that `writeSnapshot` saves.
 */
export function hashWorld(world: World): number {
  let h = FNV_OFFSET;
  h = fnvU32(h, world.seq);
  h = fnvU32(h, world.time);
  h = fnvU32(h, world.seed);
  h = fnvSafeInt(h, world.money);
  for (const word of world.rng.state) h = fnvU32(h, word);
  const g = world.ground;
  h = fnvU32(h, g.width);
  h = fnvU32(h, g.height);
  h = fnvBytes(h, g.owned);
  h = fnvBytes(h, g.network);
  h = fnvU32(h, world.nextBuildingId);
  h = fnvU32(h, world.buildings.length);
  for (const b of world.buildings) {
    h = fnvU32(h, b.id);
    h = fnvU32(h, b.type);
    h = fnvU32(h, b.x);
    h = fnvU32(h, b.y);
    h = fnvU32(h, b.rot);
    h = fnvU32(h, b.level);
    h = fnvU32(h, b.buildRemaining);
    h = fnvU32(h, b.buildTotal);
    h = fnvSafeInt(h, b.spent);
    h = fnvU32(h, b.mode);
    h = fnvU32(h, b.flow);
    h = fnvU32(h, b.facilities);
    h = fnvU32(h, b.wear);
    h = fnvU32(h, b.usage);
    h = fnvU32(h, b.bigOnly);
  }

  h = fnvU32(h, world.nextOpsId);
  h = fnvU32(h, world.rating);
  for (const r of world.ratings) h = fnvU32(h, r);
  h = fnvU32(h, world.buzz + 1000);
  h = fnvU32(h, world.milestones);
  for (const n of world.departuresByClass) h = fnvU32(h, n);
  h = fnvU32(h, world.runwayClosures.length);
  for (const c of world.runwayClosures) {
    h = fnvU32(h, c.runwayId);
    h = fnvU32(h, c.from);
    h = fnvU32(h, c.until);
    h = fnvU32(h, c.kind);
  }
  h = fnvU32(h, world.windDir);
  h = fnvU32(h, world.windSpeed);
  h = fnvU32(h, world.oilIndex);
  h = fnvU32(h, world.economy + 1000);
  h = fnvU32(h, world.marketFlags);
  h = fnvU32(h, world.adminMode);
  h = fnvU32(h, world.noiseInsulation);
  h = fnvU32(h, world.legacyWideStands ? 1 : 0);
  h = fnvU32(h, world.theme);
  h = fnvU32(h, world.dailyGoals.length);
  for (const g of world.dailyGoals) {
    h = fnvU32(h, g.kind);
    h = fnvSafeInt(h, g.target);
    h = fnvSafeInt(h, g.reward);
    h = fnvU32(h, g.state);
  }
  h = fnvU32(h, world.vipsPleased);
  h = fnvU32(h, world.airshows);
  h = fnvU32(h, world.airlinesSeen.length);
  for (const id of world.airlinesSeen) h = fnvU32(h, id);
  h = fnvU32(h, world.sky);
  h = fnvU32(h, world.skyAhead.length);
  for (const s of world.skyAhead) h = fnvU32(h, s);
  h = fnvU32(h, world.visibility);
  h = fnvU32(h, world.climate);
  h = fnvU32(h, world.grantState);
  h = fnvSafeInt(h, world.grantAmount);
  h = fnvU32(h, world.grantDeadline);
  h = fnvU32(h, world.airportName.length);
  for (let i = 0; i < world.airportName.length; i++) {
    h = fnvU32(h, world.airportName.charCodeAt(i));
  }
  h = fnvU32(h, world.staffAuto);
  for (const n of world.staffHired) h = fnvU32(h, n);
  for (const n of world.staffNight) h = fnvU32(h, n);
  h = fnvU32(h, world.wagePct);
  h = fnvU32(h, world.strikeUntil);
  h = fnvU32(h, world.nightNoise);
  h = fnvU32(h, world.snowfall);
  h = fnvU32(h, world.blizzardAt);
  h = fnvU32(h, world.blizzardUntil);
  h = fnvU32(h, world.runwaySnow.length);
  for (const s of world.runwaySnow) {
    h = fnvU32(h, s.runwayId);
    h = fnvU32(h, s.depth);
  }
  h = fnvU32(h, world.snowJobs.length);
  for (const j of world.snowJobs) {
    h = fnvU32(h, j.runwayId);
    h = fnvU32(h, j.baseId);
    h = fnvU32(h, j.team);
    h = fnvU32(h, j.from);
    h = fnvU32(h, j.arrive);
    h = fnvU32(h, j.turn);
    h = fnvU32(h, j.done);
    h = fnvU32(h, j.home);
    h = fnvU32(h, j.end);
  }
  h = fnvU32(h, world.socialReadyAt);
  h = fnvU32(h, world.promoUntil);
  h = fnvU32(h, world.transferPool.length);
  for (const g of world.transferPool) {
    h = fnvU32(h, g.at);
    h = fnvU32(h, g.n);
  }
  h = fnvU32(h, world.landingFeePct);
  h = fnvU32(h, world.paxFeePct);
  h = fnvU32(h, world.loans.length);
  for (const l of world.loans) {
    h = fnvU32(h, l.id);
    h = fnvSafeInt(h, l.principal);
    h = fnvSafeInt(h, l.remaining);
    h = fnvSafeInt(h, l.payment);
  }
  h = hashLedger(h, world.today);
  h = hashLedger(h, world.yesterday);
  h = hashLedger(h, world.allTime);
  h = fnvU32(h, world.offers.length);
  for (const o of world.offers) {
    for (const v of [
      o.id,
      o.airline,
      o.cls,
      o.flightsPerDay,
      o.slotOffset,
      o.days,
      o.expiresAt,
      o.renewal,
      o.slots.length,
      ...o.slots,
      o.cargo,
      o.event,
      o.rental,
    ]) {
      h = fnvU32(h, v);
    }
  }
  h = fnvU32(h, world.contracts.length);
  for (const c of world.contracts) {
    for (const v of [
      c.id,
      c.airline,
      c.cls,
      c.endsAt,
      c.served,
      c.cancelled,
      c.score,
      c.strikes,
      c.cargo,
      c.event,
      c.rental,
      ...REPORT_FIELDS.map((k) => c.report[k]),
      ...c.slots,
    ]) {
      h = fnvU32(h, v);
    }
    h = fnvSafeInt(h, c.revenue);
  }
  h = fnvU32(h, world.flights.length);
  for (const f of world.flights) {
    for (const v of [
      f.id,
      f.contractId,
      f.airline,
      f.cls,
      f.scheduledAt,
      f.status,
      f.holdReason,
      f.aircraftId,
      f.standId,
      f.finishedAt,
      f.departAt,
      f.paxIn,
      f.paxOut,
      f.paxReady,
      f.paxCheckedIn,
      f.paxScreened,
      f.paxArrived,
      f.paxBoarded,
      f.waitSum,
      f.offBlockAt,
      f.gateClosed,
      f.satisfaction,
      f.lostArrival,
      f.lostVehicles,
      f.lostPassengers,
      f.lostDeparture,
      f.lostTechnical,
      f.cargo,
      f.lateBy,
      f.transferIn,
      f.transferOut,
      f.event,
      f.lostWeather,
      f.park,
      f.vip,
    ]) {
      h = fnvU32(h, v);
    }
    h = fnvSafeInt(h, f.revenue);
  }
  h = fnvU32(h, world.aircraft.length);
  for (const a of world.aircraft) {
    for (const v of [
      a.id,
      a.flightId,
      a.cls,
      a.airline,
      a.phase,
      a.runwayId,
      a.runwayDir,
      a.runwayIndex,
      a.runwayPos,
      a.speed,
      a.pathPos,
      a.standId,
      a.timer,
      a.timerTotal,
      a.boardState,
      a.boardMilli,
      a.fuelFromDepot,
      a.gse,
      a.contracted,
      a.deiceLeft,
      a.deiceTotal,
      a.techLeft,
      a.techTotal,
      a.crewEta,
      a.emergency,
      ...a.gseEta,
      ...a.svcLeft,
      ...a.svcTotal,
      a.path.length,
      ...a.path,
    ]) {
      h = fnvU32(h, v);
    }
  }
  return h;
}
